/**
 * Tap-to-zoom for a photo: a thumbnail button that opens the photo full size in a dialog. It
 * replaces the hover card on inspection screens, which a phone can never open.
 *
 * Storage URLs are signed on the way in (the inspection bucket is private); external URLs (the SBT
 * listing photo) pass straight through `useSignedUrl`.
 */

import { useState } from 'react';
import { ImageOff } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { SignedImage } from '@/components/ui/signed-image';
import { cn } from '@/lib/utils';

interface PhotoThumbProps {
    url: string;
    /** A local object URL to show instead of the stored one, while the stored one is fresh. */
    previewUrl?: string | null;
    alt: string;
    className?: string;
    imgClassName?: string;
    /** Shown on a phone where the thumbnail is too small for a caption of its own. */
    caption?: string;
}

function Fallback({ className }: { className?: string }) {
    return (
        <span className={cn('flex h-full w-full items-center justify-center bg-muted text-muted-foreground', className)}>
            <ImageOff className="h-4 w-4" aria-hidden />
        </span>
    );
}

export function PhotoThumb({ url, previewUrl, alt, className, imgClassName, caption }: PhotoThumbProps) {
    const [open, setOpen] = useState(false);
    const src = previewUrl || url;
    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className={cn(
                    'relative shrink-0 overflow-hidden rounded-md border bg-muted cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    className ?? 'h-14 w-14',
                )}
                aria-label={`Open photo: ${alt}`}
            >
                <SignedImage src={src} alt={alt} className={cn('h-full w-full object-cover', imgClassName)} fallback={<Fallback />} />
            </button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-[min(96vw,64rem)] gap-2 p-2 sm:p-3" aria-describedby={undefined}>
                    <DialogTitle className="pr-8 text-sm font-medium">{caption ?? alt}</DialogTitle>
                    {open && (
                        <SignedImage
                            src={src}
                            alt={alt}
                            className="mx-auto max-h-[80vh] w-auto rounded-md object-contain"
                            fallback={<div className="py-16 text-center text-sm text-muted-foreground">Loading photo…</div>}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

/** A row of thumbnails, each tap-to-zoom. */
export function PhotoStrip({
    photos,
    label,
    className,
    size = 'h-14 w-14',
}: {
    photos: Array<{ id: number | string; url: string; caption?: string }>;
    label: string;
    className?: string;
    size?: string;
}) {
    if (photos.length === 0) return null;
    return (
        <div className={cn('flex flex-wrap gap-2', className)}>
            {photos.map((p, i) => (
                <PhotoThumb key={p.id} url={p.url} alt={p.caption ?? `${label} ${i + 1}`} caption={p.caption} className={size} />
            ))}
        </div>
    );
}
